import { useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { authService } from '@/services/authService'
import { RoleLabel, TypeLabel } from '@/components/ui/labels'
import TOTPEnableModal, { type TOTPEnableModalRef } from '@/components/modals/TOTPEnableModal'
import TOTPDisableModal, { type TOTPDisableModalRef } from '@/components/modals/TOTPDisableModal'
import { dateLocaleFor } from '@/lib/dateLocale'

export default function ProfilePage() {
  const { t, i18n } = useTranslation('settings')
  const { t: tReset } = useTranslation('passwordReset')
  const [searchParams] = useSearchParams()
  const { user, updateUser } = useAuth()
  // Redirected here rather than having navigated, so the page has to say why.
  const totpRequired =
    Boolean(user?.must_enrol_totp) || searchParams.get('reason') === 'totp_required'
  // Sent here from license issuance, which needs a second factor (BAFTT-13).
  const totpForIssuance = !totpRequired && searchParams.get('reason') === 'issuance_totp'
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const totpEnableModalRef = useRef<TOTPEnableModalRef>(null)
  const totpDisableModalRef = useRef<TOTPDisableModalRef>(null)

  // Look the role up by name rather than listing the cases. The switch this
  // replaces had no branch for 'admin', so an admin's role fell through to the
  // raw value and stayed English while the rest of the page was translated.
  // A role we have no translation for still shows its own name, which is the
  // right answer for a value the backend added and the locales have not caught
  // up with.
  const getRoleDisplayName = (role: string) => t(`profile.role.${role}`, { defaultValue: role })

  const getTypeDisplayName = (type: string) => t(`profile.type.${type}`, { defaultValue: type })

  const formatDate = (dateString: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      const locale = dateLocaleFor(i18n.language)

      const formattedDate = date.toLocaleString(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })

      return formattedDate
    } catch {
      return dateString
    }
  }

  const handleEditName = () => {
    setEditedName(user?.name || '')
    setIsEditingName(true)
    setError('')
    setSuccess('')
  }

  const handleCancelEdit = () => {
    setIsEditingName(false)
    setEditedName('')
    setError('')
  }

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      setError(t('profile.errors.nameRequired', 'Display name is required'))
      return
    }

    if (editedName.trim() === user?.name) {
      setIsEditingName(false)
      return
    }

    try {
      setIsSaving(true)
      setError('')

      const updatedUser = await authService.updateProfile({ name: editedName.trim() })
      updateUser(updatedUser)

      setSuccess(t('profile.success.nameUpdated', 'Display name updated successfully'))
      setIsEditingName(false)

      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, errorMessage))
      } else {
        setError(errorMessage)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleEnableTOTP = () => {
    setError('')
    totpEnableModalRef.current?.open()
  }

  const handleTOTPSuccess = async () => {
    // Refresh user profile to get updated totp_enabled status
    const updatedUser = await authService.getProfile()
    updateUser(updatedUser)
    setSuccess(t('profile.2fa.success.enabled', '2FA enabled successfully'))
    setTimeout(() => setSuccess(''), 3000)
  }

  const handleDisableTOTP = () => {
    setError('')
    totpDisableModalRef.current?.open()
  }

  const handleTOTPDisableSuccess = async () => {
    // Refresh user profile to get updated totp_enabled status
    const updatedUser = await authService.getProfile()
    updateUser(updatedUser)
    setSuccess(t('profile.2fa.success.disabled', '2FA disabled successfully'))
    setTimeout(() => setSuccess(''), 3000)
  }

  const displayUser = user

  if (!displayUser) {
    return null
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('profile.title', 'Profile')}</h1>
        <p className="text-gray-600 mt-2">{t('profile.description', 'Manage your personal information')}</p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3 animate-[fadeIn_0.3s_ease-in-out]">
          <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-green-700 flex-1">{success}</p>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 animate-[fadeIn_0.3s_ease-in-out]">
          <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <p className="text-sm text-red-700 flex-1">{error}</p>
        </div>
      )}

      {/* Profile Information */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
        {/* User Profile - Horizontal Layout */}
        <div className="flex items-center gap-6 pb-8 border-b border-gray-200">
          {/* Avatar */}
          <div className="shrink-0 w-20 h-20 bg-primary rounded-full flex items-center justify-center shadow-lg">
            <span className="text-white text-2xl font-bold">
              {displayUser.name ? displayUser.name.charAt(0).toUpperCase() : 'U'}
            </span>
          </div>

          {/* User Info */}
          <div className="flex-1 min-w-0">
            {isEditingName ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-lg font-semibold"
                  placeholder={t('profile.namePlaceholder', 'Enter display name')}
                  disabled={isSaving}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveName}
                    disabled={isSaving}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                  >
                    {isSaving && (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    )}
                    <span>{t('profile.save', 'Save')}</span>
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 text-sm font-medium"
                  >
                    {t('profile.cancel', 'Cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">{displayUser.name}</h3>
                  {displayUser.type === 'local' && (
                    <button
                      onClick={handleEditName}
                      className="px-3 py-1 text-primary border border-primary/30 rounded-md hover:bg-primary/10 transition-all text-sm font-medium"
                    >
                      {t('profile.edit', 'Edit')}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <RoleLabel
                    role={displayUser.platform_role}
                    text={getRoleDisplayName(displayUser.platform_role)}
                    size="sm"
                  />
                  <TypeLabel
                    type={displayUser.type}
                    text={getTypeDisplayName(displayUser.type)}
                    size="sm"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Account Details */}
        <div className="pt-8 space-y-4">
          <div className="flex items-center py-3 border-b border-gray-100">
            <span className="text-gray-600 text-sm font-medium w-32">{t('profile.account', 'Account')}</span>
            <span className="text-gray-900 text-base">{displayUser.account}</span>
          </div>

          {displayUser.external_id && (
            <div className="flex items-center py-3 border-b border-gray-100">
              <span className="text-gray-600 text-sm font-medium w-32">{t('profile.externalId', 'External ID')}</span>
              <span className="text-gray-900 text-base">{displayUser.external_id}</span>
            </div>
          )}

          {displayUser.last_login_at && (
            <div className="flex items-center py-3">
              <span className="text-gray-600 text-sm font-medium w-32">{t('profile.lastLogin', 'Last Login')}</span>
              <span className="text-gray-900 text-base">{formatDate(displayUser.last_login_at)}</span>
            </div>
          )}
        </div>
      </div>

      {totpRequired && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm font-semibold text-amber-900">
            {tReset('enrol.title', 'Two-factor authentication is required for your role')}
          </p>
          <p className="text-sm text-amber-800 mt-1">
            {tReset(
              'enrol.reason',
              'Administrative accounts can reach everything on this platform, so a password alone is not enough. Set up an authenticator app below to continue.'
            )}
          </p>
        </div>
      )}

      {totpForIssuance && !displayUser.totp_enabled && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-sm font-semibold text-amber-900">
            {tReset('enrol.issuanceTitle', 'License issuance needs two-factor authentication')}
          </p>
          <p className="text-sm text-amber-800 mt-1">
            {tReset(
              'enrol.issuanceReason',
              'Set up an authenticator app below. Issuing a license will then ask for a code from it.'
            )}
          </p>
        </div>
      )}

      {/* Two-Factor Authentication Section */}
      <div className="mt-6 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        {/* Section Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-primary/5 to-transparent border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900">{t('profile.2fa.title', 'Two-Factor Authentication')}</h2>
                <p className="text-sm text-gray-600 mt-0.5">{t('profile.2fa.subtitle', 'Extra security for your account')}</p>
              </div>
            </div>

            {/* Status Badge */}
            {displayUser.totp_enabled ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t('profile.2fa.enabled', 'Enabled')}
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                {t('profile.2fa.disabled', 'Disabled')}
              </span>
            )}
          </div>
        </div>

        {/* 2FA Content */}
        <div className="p-6">
          {displayUser.totp_enabled ? (
            /* TOTP Enabled State */
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {t('profile.2fa.enabledDescription', 'Your account is protected with TOTP. You need a code from your authenticator app to sign in.')}
              </p>
              <button
                onClick={handleDisableTOTP}
                className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 rounded-lg hover:bg-red-100 transition-all text-sm font-medium"
              >
                {t('profile.2fa.disable', 'Disable')}
              </button>
            </div>
          ) : (
            /* TOTP Disabled State */
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {t('profile.2fa.disabledDescription', 'Protect your account with an authenticator app (Google Authenticator, Authy, etc.).')}
              </p>
              <button
                onClick={handleEnableTOTP}
                className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-all text-sm font-medium shadow-sm hover:shadow"
              >
                {t('profile.2fa.enable', 'Enable 2FA')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Additional Info */}
      {displayUser.type !== 'local' && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-amber-400 mr-3 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-amber-800">{t('profile.externalUserNote.title', 'External Authentication User')}</h3>
              <p className="text-sm text-amber-700 mt-1">{t('profile.externalUserNote.description', 'Some profile settings are managed by your external authentication provider.')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <TOTPEnableModal ref={totpEnableModalRef} onSuccess={handleTOTPSuccess} />
      <TOTPDisableModal ref={totpDisableModalRef} onSuccess={handleTOTPDisableSuccess} />
    </div>
  )
}
